import {
  PRODUCT_IMAGES_MAX,
  adminProductSchema,
  stockMovementSchema,
  type AdminProduct,
  type AdminVariant,
  type FeePlan,
  type ProductAudience,
  type ProductKind,
  type StockMovement,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageGallery, useImagesBusy } from '../media/ImageUpload';
import { centsToInput, formatEuros, KIND_LABEL, parseEuros, REASON_LABEL } from './labels';

const stockText = (p: AdminProduct) =>
  p.kind === 'membership_fee'
    ? '—'
    : p.unlimitedStock
      ? 'Ilimitado'
      : String(p.variants.filter((v) => v.active).reduce((n, v) => n + (v.stock ?? 0), 0));

/** Who sees and who buys (F-012). */
const AUDIENCE_LABEL: Record<ProductAudience, string> = {
  all: 'Todos',
  associates: 'Exclusivo para sócios (todos veem, só sócios compram)',
  secret: 'Secreto (só sócios com sessão o veem e compram)',
};

const COLUMNS: Column<AdminProduct>[] = [
  {
    key: 'name',
    header: 'Produto',
    cell: (p) => (
      <span className="flex flex-col">
        <span className="font-semibold">{p.name}</span>
        <span className="text-caption text-gu-ink-muted">
          {KIND_LABEL[p.kind]} · {formatEuros(p.priceCents)}
          {p.associateDiscountPct > 0 ? ` · -${String(p.associateDiscountPct)}% assoc.` : ''}
          {p.streamerGift ? ' · camisola de streamer' : ''}
          {p.audience === 'associates' ? ' · exclusivo sócios' : ''}
          {p.audience === 'secret' ? ' · secreto' : ''}
        </span>
      </span>
    ),
  },
  { key: 'stock', header: 'Stock', cell: stockText, align: 'right' },
  {
    key: 'active',
    header: 'Estado',
    cell: (p) =>
      p.active ? (
        <StatusBadge tone="success">À venda</StatusBadge>
      ) : (
        <StatusBadge tone="neutral">Escondido</StatusBadge>
      ),
    align: 'right',
  },
];

/** Catalogue: products, sizes and stock. */
export function ProductsView() {
  const [products, setProducts] = useState<AdminProduct[] | null>(null);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/store/products', z.array(adminProductSchema))
      .then(setProducts)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const product = products?.find((p) => p.id === selected) ?? null;
  const onSaved = (saved: AdminProduct) => {
    setSelected(saved.id);
    load();
  };

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[26rem_minmax(0,1fr)]">
      <Card
        headingLevel={2}
        title="Produtos"
        actions={
          <Button size="sm" onClick={() => setSelected('new')}>
            Novo produto
          </Button>
        }
      >
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <Table
          caption="Produtos da loja"
          columns={COLUMNS}
          rows={products ?? []}
          rowKey={(p) => p.id}
          empty={products ? 'Ainda não há produtos.' : 'A carregar…'}
          onRowClick={(p) => setSelected(p.id)}
        />
      </Card>
      {selected === 'new' ? (
        <ProductForm key="new" product={null} onSaved={onSaved} />
      ) : product ? (
        <div className="flex flex-col gap-4">
          <ProductForm key={product.id} product={product} onSaved={onSaved} />
          {product.kind !== 'membership_fee' ? (
            <VariantsCard product={product} onChanged={onSaved} />
          ) : (
            <FeeOptionsCard
              product={product}
              jerseys={(products ?? []).filter((p) => p.kind !== 'membership_fee')}
              onChanged={onSaved}
            />
          )}
        </div>
      ) : (
        <p className="text-gu-ink-muted">Escolha um produto ou crie um novo.</p>
      )}
    </div>
  );
}

function ProductForm({
  product,
  onSaved,
}: {
  product: AdminProduct | null;
  onSaved: (p: AdminProduct) => void;
}) {
  const [name, setName] = useState(product?.name ?? '');
  const [slug, setSlug] = useState(product?.slug ?? '');
  const [kind, setKind] = useState<ProductKind>(product?.kind ?? 'merch');
  const [description, setDescription] = useState(product?.description ?? '');
  const [price, setPrice] = useState(product ? centsToInput(product.priceCents) : '');
  const [discount, setDiscount] = useState(String(product?.associateDiscountPct ?? 0));
  const [personalization, setPersonalization] = useState(product?.personalization ?? false);
  const [personalizationPrice, setPersonalizationPrice] = useState(
    product ? centsToInput(product.personalizationPriceCents) : '0,00',
  );
  const [images, setImages] = useState<string[]>(product?.images ?? []);
  const [active, setActive] = useState(product?.active ?? false);
  const [streamerGift, setStreamerGift] = useState(product?.streamerGift ?? false);
  const [unlimitedStock, setUnlimitedStock] = useState(product?.unlimitedStock ?? false);
  const [audience, setAudience] = useState<ProductAudience>(product?.audience ?? 'all');
  const [sizes, setSizes] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();
  const fee = kind === 'membership_fee';

  const autoSlug = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    const priceCents = parseEuros(price);
    const persCents = parseEuros(personalizationPrice);
    const pct = Number(discount);
    const problems: Record<string, string> = {};
    if (priceCents === null) problems.priceCents = 'Preço em euros, ex.: 25,00';
    if (personalization && persCents === null)
      problems.personalizationPriceCents = 'Preço em euros';
    if (!Number.isInteger(pct) || pct < 0 || pct > 100) problems.associateDiscountPct = '0 a 100';
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    const body = {
      slug,
      name,
      description,
      kind,
      priceCents,
      associateDiscountPct: fee ? 0 : pct,
      personalization: fee ? false : personalization,
      personalizationPriceCents: personalization ? persCents : 0,
      streamerGift: fee ? false : streamerGift,
      unlimitedStock: fee ? false : unlimitedStock,
      audience: fee ? 'all' : audience,
      images,
      active,
      sortOrder: product?.sortOrder ?? 0,
    };
    setBusy(true);
    setError(null);
    try {
      const result = product
        ? await send('PUT', `/api/site/store/products/${product.id}`, body)
        : await send('POST', '/api/site/store/products', {
            product: body,
            variants: sizes
              .split(/[,;]/)
              .map((s) => s.trim())
              .filter(Boolean)
              .map((label) => ({ label, sku: null })),
          });
      setSaved(true);
      onSaved(adminProductSchema.parse(result));
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title={product ? product.name : 'Novo produto'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Nome">
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!product) setSlug(autoSlug(e.target.value));
              }}
            />
          </Field>
          <Field label="Endereço" hint={`growup.gg/loja/${slug || '…'}`} error={fields.slug}>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
          </Field>
          <Field label="Tipo" error={fields.kind}>
            <Select
              value={kind}
              disabled={product !== null}
              onChange={(e) => setKind(e.target.value as ProductKind)}
            >
              {(Object.keys(KIND_LABEL) as ProductKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          {fee && product ? null : (
            <Field label="Preço (€)" error={fields.priceCents}>
              <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
          )}
          {!fee ? (
            <Field
              label="Desconto de associado (%)"
              hint="Só para associados com quota em dia."
              error={fields.associateDiscountPct}
            >
              <Input
                inputMode="numeric"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </Field>
          ) : null}
        </div>
        <ImageGallery
          label="Fotos"
          value={images}
          onChange={setImages}
          aspect={IMAGE_ASPECTS.product}
          onBusyChange={photos.track('fotos')}
          max={PRODUCT_IMAGES_MAX}
          error={
            fields.images ?? Object.entries(fields).find(([k]) => k.startsWith('images.'))?.[1]
          }
        />
        <Field label="Descrição" optional>
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {!product && kind === 'apparel' ? (
          <Field
            label="Tamanhos"
            hint="Separados por vírgulas, ex.: XS, S, M, L, XL. Vazio = tamanho único."
          >
            <Input value={sizes} onChange={(e) => setSizes(e.target.value)} />
          </Field>
        ) : null}
        {!fee ? (
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2">
            <Checkbox
              label="Personalização com nome e número"
              checked={personalization}
              onChange={(e) => setPersonalization(e.target.checked)}
            />
            {personalization ? (
              <Field label="Preço da personalização (€)" error={fields.personalizationPriceCents}>
                <Input
                  inputMode="decimal"
                  value={personalizationPrice}
                  onChange={(e) => setPersonalizationPrice(e.target.value)}
                />
              </Field>
            ) : null}
          </div>
        ) : null}
        {!fee ? (
          <Checkbox
            label="Camisola de streamer"
            hint="Oferecida com um código a quem faz um ano como streamer. Só um produto; pode não estar à venda no site."
            checked={streamerGift}
            onChange={(e) => setStreamerGift(e.target.checked)}
          />
        ) : null}
        {!fee ? (
          <Checkbox
            label="Stock ilimitado"
            hint={
              product && !product.unlimitedStock && unlimitedStock
                ? 'Nunca esgota. Ao guardar, o stock que houver passa a zero (fica no histórico).'
                : 'Nunca esgota e não conta stock: produtos digitais ou feitos por encomenda.'
            }
            checked={unlimitedStock}
            onChange={(e) => setUnlimitedStock(e.target.checked)}
          />
        ) : null}
        {!fee ? (
          <Field label="Quem vê e quem compra" error={fields.audience}>
            <Select
              value={audience}
              onChange={(e) => setAudience(e.target.value as ProductAudience)}
            >
              {(Object.keys(AUDIENCE_LABEL) as ProductAudience[]).map((a) => (
                <option key={a} value={a}>
                  {AUDIENCE_LABEL[a]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Checkbox
          label="À venda no site"
          hint={fee ? 'Só pode haver uma quota à venda.' : undefined}
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {saved ? <Notice tone="success">Guardado.</Notice> : null}
        <div>
          <Button type="submit" disabled={busy || photos.busy}>
            {product ? 'Guardar' : 'Criar produto'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const FEE_PLAN_HINT: Record<FeePlan, string> = {
  annual: 'Vale para o ano civil; paga a partir de 1 de novembro, vale também para o seguinte.',
  annual_jersey:
    'Anual com a camisola: quem compra escolhe o tamanho, que sai do stock da camisola. A encomenda fica "Paga" até a marcar entregue.',
  monthly:
    'Cada pagamento conta para este ano; o primeiro já torna associado até 31/12. Cobrar os meses em falta é manual.',
};

/** The fee's three options (F-001): own price, shown or hidden, and which jersey. */
function FeeOptionsCard({
  product,
  jerseys,
  onChanged,
}: {
  product: AdminProduct;
  jerseys: AdminProduct[];
  onChanged: (p: AdminProduct) => void;
}) {
  return (
    <Card headingLevel={2} title="Opções da quota">
      <ul className="flex flex-col divide-y divide-gu-line border-y border-gu-line">
        {product.variants.map((v) => (
          <FeeOption key={v.id} variant={v} jerseys={jerseys} onChanged={onChanged} />
        ))}
      </ul>
    </Card>
  );
}

function FeeOption({
  variant,
  jerseys,
  onChanged,
}: {
  variant: AdminVariant;
  jerseys: AdminProduct[];
  onChanged: (p: AdminProduct) => void;
}) {
  const [price, setPrice] = useState(centsToInput(variant.priceCents ?? 0));
  const [jersey, setJersey] = useState(variant.jerseyProductId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const plan = variant.feePlan ?? 'annual';

  const save = async (active: boolean) => {
    const priceCents = parseEuros(price);
    if (priceCents === null) return setError('Preço em euros, ex.: 30,00');
    setError(null);
    setBusy(true);
    try {
      onChanged(
        adminProductSchema.parse(
          await send('PUT', `/api/site/store/variants/${variant.id}`, {
            label: variant.label,
            sku: variant.sku,
            active,
            sortOrder: variant.sortOrder,
            priceCents,
            jerseyProductId: plan === 'annual_jersey' && jersey ? jersey : null,
          }),
        ),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">
          {variant.label}
          {!variant.active ? (
            <span className="ml-2 text-caption text-gu-ink-muted">(escondida)</span>
          ) : null}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => void save(!variant.active)}
        >
          {variant.active ? 'Esconder' : 'Mostrar'}
        </Button>
      </div>
      <p className="text-caption text-gu-ink-muted">{FEE_PLAN_HINT[plan]}</p>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void save(variant.active);
        }}
      >
        <Field label={`Preço de ${variant.label} (€)`}>
          <Input
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-28"
          />
        </Field>
        {plan === 'annual_jersey' ? (
          <Field label="Camisola">
            <Select value={jersey} onChange={(e) => setJersey(e.target.value)}>
              <option value="">Escolhe o produto…</option>
              {jerseys.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {!p.active ? ' (escondido)' : ''}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Button type="submit" variant="secondary" disabled={busy}>
          Guardar
        </Button>
      </form>
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </li>
  );
}

function VariantsCard({
  product,
  onChanged,
}: {
  product: AdminProduct;
  onChanged: (p: AdminProduct) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      onChanged(adminProductSchema.parse(await action()));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    }
  };

  const update = (v: AdminVariant, patch: Partial<AdminVariant>) =>
    void run(() =>
      send('PUT', `/api/site/store/variants/${v.id}`, {
        label: v.label,
        sku: v.sku,
        active: v.active,
        sortOrder: v.sortOrder,
        ...patch,
      }),
    );

  return (
    <Card headingLevel={2} title={product.unlimitedStock ? 'Tamanhos' : 'Tamanhos e stock'}>
      <div className="flex flex-col gap-3">
        <ul className="flex flex-col divide-y divide-gu-line border-y border-gu-line">
          {product.variants.map((v) => (
            <li key={v.id} className="flex flex-col gap-2 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">
                  {v.label}
                  {v.sku ? (
                    <span className="ml-2 text-caption text-gu-ink-muted">{v.sku}</span>
                  ) : null}
                  {!v.active ? (
                    <span className="ml-2 text-caption text-gu-ink-muted">(escondido)</span>
                  ) : null}
                </span>
                <span className="flex items-center gap-2">
                  {v.stock === null ? (
                    <span aria-label={`Stock de ${v.label}`}>Ilimitado</span>
                  ) : (
                    <>
                      <span aria-label={`Stock de ${v.label}`}>{v.stock} un.</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setOpenId(openId === v.id ? null : v.id)}
                      >
                        {openId === v.id ? 'Fechar' : 'Stock'}
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => update(v, { active: !v.active })}
                  >
                    {v.active ? 'Esconder' : 'Mostrar'}
                  </Button>
                </span>
              </div>
              {openId === v.id && v.stock !== null ? (
                <StockPanel variant={v} onChanged={(p) => onChanged(p)} />
              ) : null}
            </li>
          ))}
        </ul>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() =>
              send('POST', `/api/site/store/products/${product.id}/variants`, {
                label: newLabel,
                sku: null,
                active: true,
                sortOrder: product.variants.length,
              }),
            ).then((ok) => ok && setNewLabel(''));
          }}
        >
          <Field label="Novo tamanho">
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
          </Field>
          <Button type="submit" variant="secondary" disabled={!newLabel.trim()}>
            Acrescentar
          </Button>
        </form>
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

function StockPanel({
  variant,
  onChanged,
}: {
  variant: AdminVariant;
  onChanged: (p: AdminProduct) => void;
}) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState<'restock' | 'adjustment'>('restock');
  const [note, setNote] = useState('');
  const [movements, setMovements] = useState<StockMovement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadMovements = useCallback(() => {
    getJson(`/api/site/store/variants/${variant.id}/movements`, z.array(stockMovementSchema))
      .then(setMovements)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [variant.id]);
  useEffect(loadMovements, [loadMovements, variant.stock]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = Number(delta);
    if (!Number.isInteger(n) || n === 0)
      return setError('Um número inteiro, positivo ou negativo.');
    setError(null);
    try {
      const updated = await send('POST', `/api/site/store/variants/${variant.id}/stock`, {
        delta: n,
        reason,
        note: note || null,
      });
      setDelta('');
      setNote('');
      onChanged(adminProductSchema.parse(updated));
    } catch (err) {
      setError(messageOf(err));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded border border-gu-line p-3">
      <form
        className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[6rem_9rem_minmax(0,1fr)_auto]"
        onSubmit={(e) => void submit(e)}
      >
        <Field label="Quantidade" hint="-2 tira">
          <Input inputMode="numeric" value={delta} onChange={(e) => setDelta(e.target.value)} />
        </Field>
        <Field label="Motivo">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value as 'restock' | 'adjustment')}
          >
            <option value="restock">Entrada</option>
            <option value="adjustment">Acerto de inventário</option>
          </Select>
        </Field>
        <Field label="Nota" optional>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button type="submit">Registar</Button>
      </form>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto text-caption text-gu-ink-muted">
        {movements?.map((m) => (
          <li key={m.id} className="flex justify-between gap-2">
            <span>
              {formatDateTime(m.createdAt)} · {REASON_LABEL[m.reason]}
              {m.orderNumber ? ` ${m.orderNumber}` : ''}
              {m.note ? ` · ${m.note}` : ''}
            </span>
            <span className="font-mono text-gu-ink">
              {m.delta > 0 ? '+' : ''}
              {m.delta}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
